// fetchLiveStreamSessionLeaderboard.js
'use strict';
import parseLinkHeader from 'parse-link-header';
import debounce from 'lodash/debounce';

import fetch from '../resource/customFetch.js';
import {
  ADD_LIST_ITEMS,
  SET_LIST_ITEMS,
  MERGE_LIVE_STREAM_DATA,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getTimestampOffset, {
  Accuracy,
} from '../selector/getTimestampOffset.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import objectifyArrayById from '../resource/objectifyArrayById.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import handleFetchError from '../resource/handleFetchError.js';
import getCurrentUnixTimestamp from '../resource/getCurrentUnixTimestamp.js';
import {
  liveStreamSessionLeaderboardCategory,
  TOP_LEADERBOARD_USER_LEAST,
} from '../resource/liveStreamConstants.js';
import { SET_LIVE_STREAM_DATA } from '../ActionTypes.js';

const debounceFunctionMap = {};

const debouncedFetchLiveStreamSessionLeaderboard =
  (payload = {}) =>
  async dispatch => {
    const {
      streamId,
      sessionId,
      category = liveStreamSessionLeaderboardCategory.GIFTS,
    } = payload;
    const key = `${streamId}:${sessionId}:${category}`;
    if (!debounceFunctionMap[key]) {
      debounceFunctionMap[key] = debounce(
        payload => dispatch(fetchLiveStreamSessionLeaderboardCore(payload)),
        500 // TODO: remote config
      );
    }
    return debounceFunctionMap[key](payload);
  };

/**
 * Fetch live stream session leader board
 * @kind action
 * @param {string} {streamId} - stream id
 * @param {string} {sessionId} - livestream session id
 * @param {string} {category} - leaderboard category 'views', 'gifts' or null.
 * @param {string} {query} - search query.
 * @param {number} {page} - page number.
 * @param {number} {limit} - page size.
 * @param {number} {unixTimestamp} - unix timestamp.
 * @return {Promise} Action promise.
 */

export const fetchLiveStreamSessionLeaderboardCore =
  ({
    streamId,
    sessionId,
    category = liveStreamSessionLeaderboardCategory.GIFTS,
    query,
    limit = TOP_LEADERBOARD_USER_LEAST,
    page = 1,
    unixTimestamp,
  }) =>
  async (dispatch, getState) => {
    if (!streamId || !sessionId) return dispatch({ type: '' });

    const hasQuery = !!query;
    const currentUnixTimestamp = getLiveStreamData(
      getState(),
      [streamId, 'session', sessionId],
      'leaderboardTimestamp'
    );

    if (!hasQuery && unixTimestamp && unixTimestamp <= currentUnixTimestamp) {
      return dispatch({ type: '' });
    } else if (unixTimestamp) {
      dispatch({
        type: SET_LIVE_STREAM_DATA,
        payload: {
          selectPath: [streamId, 'session', sessionId, 'leaderboardTimestamp'],
          data: unixTimestamp,
        },
      });
    }

    const timestampOffsetSeconds = getTimestampOffset(
      getState(),
      Accuracy.SECOND
    );
    const timestamp =
      unixTimestamp ||
      currentUnixTimestamp ||
      getCurrentUnixTimestamp({
        offsetSeconds: timestampOffsetSeconds,
      });
    const selectPath = hasQuery
      ? [streamId, 'session', sessionId, 'leaderboard', category, query]
      : [streamId, 'session', sessionId, 'leaderboard', category];
    const listSelectPath = ['liveStream', ...selectPath];
    const networkingSelectPath = ['liveStream', ...selectPath, page];
    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
      },
    };

    const url = getResourceUrl({
      endpoint: `/sessions/${sessionId}/leaderboard`,
    });
    if (hasQuery) {
      url.searchParams.set('q', query);
    }
    if (category !== liveStreamSessionLeaderboardCategory.TOTAL) {
      url.searchParams.append('category', category);
    }
    url.searchParams.append('limit', limit);
    url.searchParams.append('page', page);
    url.searchParams.append('_', timestamp);

    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: networkingSelectPath },
    });
    try {
      const response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        await handleFetchError({ response });
      }
      const totalCount = +response.headers.get('x-total-count');

      const links = parseLinkHeader(response.headers.get('Link'));
      const nextPage = links && links.next && parseInt(links.next.page, 10);
      const lastPage = links && links.last && parseInt(links.last.page, 10);

      const payload = await response.json();
      const data = objectifyArrayById({ array: payload });
      const itemIds = Object.keys(data);
      const listActionType = page === 1 ? SET_LIST_ITEMS : ADD_LIST_ITEMS;
      await Promise.all([
        dispatch({
          type: MERGE_LIVE_STREAM_DATA,
          payload: {
            selectPath,
            data,
          },
        }),
        dispatch({
          type: listActionType,
          payload: {
            selectPath: listSelectPath,
            itemIds,
            totalCount,
            nextPage,
            lastPage,
          },
        }),
      ]);
      return dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: networkingSelectPath },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: networkingSelectPath, error },
      });
    }
  };

export default debouncedFetchLiveStreamSessionLeaderboard;
